import React, { useMemo, useState } from 'react'
import * as XLSX from 'xlsx'
import {
  CBadge,
  CButton,
  CCard,
  CFormSelect,
  CLoadingButton,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
} from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import {
  cilArrowLeft,
  cilCheckCircle,
  cilCloudDownload,
  cilCloudUpload,
  cilFile,
  cilSync,
  cilWarning,
} from '@coreui/icons'
import ToastComponent from 'src/components/common/TaostComponent'
import {
  ImportErrorAlert,
  ImportFileBar,
  ImportUploadStep,
  InfoBox,
  MiniStepper,
  StatTile,
  tintStyle,
} from 'src/components/common/ImportWizard'
import {
  MATCH_META,
  buildPlayerIndex,
  readPlayerCells,
  resolvePlayer,
  takePlayerColumns,
} from 'src/components/common/playerMatching'
import { POSITION_META, hasScores } from './scorecardConfig'
import {
  DisplayValue,
  RowScores,
  parseStatCells,
  readStatCells,
  takeStatColumns,
} from './scorecardCsv'

const WIZARD_STEPS = ['Upload File', 'Validate Players', 'Review & Import']

const RESULT_META = {
  valid: { label: 'Valid', color: 'success', icon: cilCheckCircle, accent: '#198754' },
  unmatched_player: {
    label: 'Player Not In Squad',
    color: 'danger',
    icon: cilWarning,
    accent: '#dc3545',
  },
  ambiguous_player: {
    label: 'Several Players Match',
    color: 'warning',
    icon: cilWarning,
    accent: '#b45309',
  },
  duplicate: { label: 'Duplicate Player', color: 'warning', icon: cilWarning, accent: '#b45309' },
  no_stats: { label: 'No Scores In Row', color: 'warning', icon: cilWarning, accent: '#b45309' },
}

const playerLabel = (player) => {
  const position = POSITION_META[player?.player_position]
  return position ? `${player.player_name} (${position.label})` : player?.player_name || ''
}

// Turns a parsed sheet into the rows the wizard validates: which header is the player, which
// headers are stats this game scores, and what is left over. A real match export carries every
// column on the grid whether the innings used it or not, so a column that is blank the whole way
// down is dropped rather than shown as 24 empty cells to scroll past.
const readSheet = (json, rawHeaders, squadColumns) => {
  const pool = [...rawHeaders]
  const playerColumns = takePlayerColumns(pool)
  if (!playerColumns.found) {
    return { error: 'This file has no player name or player ID column.' }
  }

  const detectedColumns = takeStatColumns(pool, json, squadColumns)

  if (!detectedColumns.length) {
    return {
      error: 'None of the columns in this file have anything to import into this scorecard.',
    }
  }

  const baseRows = json
    .map((r) => ({
      ...readPlayerCells(r, playerColumns),
      cells: readStatCells(r, detectedColumns),
      manual_id: '',
    }))
    // Rows with no player on them are the blank lines spreadsheets leave behind, not data.
    .filter((r) => r.csv_name !== '' || r.csv_player_id !== '')

  if (!baseRows.length) return { error: 'This file has no rows to import.' }

  return { baseRows, detectedColumns, ignoredHeaders: pool }
}

// Re-run over the whole file after every mapping change - which player a row points at decides
// whether some other row is now a duplicate, so a single row cannot be re-checked on its own.
const buildValidatedRows = (baseRows, statColumns, index, columns) => {
  const claimed = new Set()

  return baseRows.map((base, i) => {
    const { record: player, match, ambiguous } = resolvePlayer(base, index)

    const { values, invalidCells } = parseStatCells(base.cells, statColumns)
    const valueCount = Object.keys(values).length

    let result = 'valid'
    if (!player) {
      result = ambiguous ? 'ambiguous_player' : 'unmatched_player'
    } else if (claimed.has(String(player.id))) {
      result = 'duplicate'
    } else if (!valueCount) {
      result = 'no_stats'
    }

    if (result === 'valid') claimed.add(String(player.id))

    return {
      ...base,
      row: i + 1,
      player_row_id: player ? String(player.id) : '',
      player_name: player?.player_name || '',
      player_position: player?.player_position,
      match,
      ambiguous_count: ambiguous?.length || 0,
      values,
      invalid_cells: invalidCells,
      overwrite: Boolean(player && hasScores(player, columns)),
      result,
      included: result === 'valid',
    }
  })
}

const ScorecardImport = ({ columns, squad, onImport, onCancel, inningLabel }) => {
  const [wizardStep, setWizardStep] = useState(1)
  const [maxWizardStep, setMaxWizardStep] = useState(1)

  const [fileName, setFileName] = useState('')
  const [fileRowCount, setFileRowCount] = useState(0)
  const [busy, setBusy] = useState(false)

  const [rows, setRows] = useState([])
  const [statColumns, setStatColumns] = useState([])
  const [ignoredHeaders, setIgnoredHeaders] = useState([])

  const [importing, setImporting] = useState(false)
  const [importErrors, setImportErrors] = useState([])

  const squadList = useMemo(() => squad || [], [squad])
  const squadColumns = useMemo(() => (columns || []).filter((c) => c?.attribute_key), [columns])
  const squadIndex = useMemo(
    () =>
      buildPlayerIndex(squadList, {
        getKey: (row) => row.id,
        getPlayerId: (row) => row.player_id,
        getName: (row) => row.player_name,
      }),
    [squadList],
  )

  const playerOptions = useMemo(
    () =>
      squadList
        .map((player) => ({ value: String(player.id), label: playerLabel(player) }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [squadList],
  )

  const goToStep = (n) => {
    setWizardStep(n)
    setMaxWizardStep((m) => Math.max(m, n))
  }

  const resetAll = () => {
    setWizardStep(1)
    setMaxWizardStep(1)
    setFileName('')
    setFileRowCount(0)
    setRows([])
    setStatColumns([])
    setIgnoredHeaders([])
    setImportErrors([])
  }

  // Built here rather than fetched: the template is this squad crossed with the columns this
  // game actually scores, both of which are already on screen.
  const handleDownloadTemplate = () => {
    // Same column order a match export uses, so a template and an export are interchangeable here.
    const header = ['Player', ...squadColumns.map((c) => c.label), 'Player ID']
    const body = squadList.map((player) => [
      player.player_name,
      ...squadColumns.map(() => ''),
      player.player_id ?? '',
    ])
    const sheet = XLSX.utils.aoa_to_sheet([header, ...body])
    const book = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(book, sheet, 'Scorecard')
    XLSX.writeFile(book, 'MyClubtap-ScorecardImport.csv', { bookType: 'csv' })
  }

  const handleFile = (file) => {
    if (!file) return
    setBusy(true)
    const reader = new FileReader()
    reader.onload = (evt) => {
      let json = []
      let rawHeaders = []
      try {
        const isCsv = /\.csv$/i.test(file.name)
        const workbook = XLSX.read(evt.target.result, { type: isCsv ? 'string' : 'array' })
        const sheet = workbook.Sheets[workbook.SheetNames[0]]
        json = XLSX.utils.sheet_to_json(sheet, { defval: '' })
        rawHeaders = json.length ? Object.keys(json[0]) : []
      } catch (err) {
        ToastComponent('Could not read this file. Please check the format and try again.', 'error')
        setBusy(false)
        return
      }

      if (!rawHeaders.length) {
        ToastComponent('This file has no rows to import.', 'error')
        setBusy(false)
        return
      }

      const read = readSheet(json, rawHeaders, squadColumns)
      if (read.error) {
        ToastComponent(read.error, 'error')
        setBusy(false)
        return
      }
      const { baseRows, detectedColumns, ignoredHeaders: ignored } = read

      setFileName(file.name)
      setFileRowCount(baseRows.length)
      setStatColumns(detectedColumns)
      setIgnoredHeaders(ignored)
      setRows(buildValidatedRows(baseRows, detectedColumns, squadIndex, squadColumns))
      setImportErrors([])
      setBusy(false)
    }
    if (/\.csv$/i.test(file.name)) reader.readAsText(file)
    else reader.readAsArrayBuffer(file)
  }

  const remapRow = (row, playerRowId) => {
    setRows((prev) => {
      const baseRows = prev.map((r) => (r.row === row ? { ...r, manual_id: playerRowId } : r))
      const revalidated = buildValidatedRows(baseRows, statColumns, squadIndex, squadColumns)
      // A row the admin has just pointed somewhere comes back included; every other row keeps
      // the include/exclude decision already made for it.
      return revalidated.map((r, idx) => ({
        ...r,
        included: r.result === 'valid' && (r.row === row || prev[idx].included),
      }))
    })
  }

  const toggleRowIncluded = (row) => {
    setRows((prev) =>
      prev.map((r) =>
        r.row === row && r.result === 'valid' ? { ...r, included: !r.included } : r,
      ),
    )
  }

  const summary = useMemo(() => {
    const valid = rows.filter((r) => r.result === 'valid').length
    return { valid, needsAttention: rows.length - valid }
  }, [rows])

  const readyRows = rows.filter((r) => r.included)
  const overwriteCount = readyRows.filter((r) => r.overwrite).length
  const skippedCells = rows.reduce((total, r) => total + r.invalid_cells, 0)

  const runImport = async () => {
    setImporting(true)
    setImportErrors([])
    try {
      const res = await onImport(readyRows.map((r) => ({ id: r.player_row_id, values: r.values })))
      if (res?.success) {
        resetAll()
      } else {
        setImportErrors(
          Array.isArray(res?.errors) && res.errors.length
            ? res.errors
            : ['Something went wrong. Please try again.'],
        )
      }
    } catch (e) {
      setImportErrors(['Something went wrong. Please try again.'])
    } finally {
      setImporting(false)
    }
  }

  const renderUpload = () => (
    <>
      <ImportUploadStep
        templateHint="The template lists every player in this squad with the stat columns this game scores, ready to fill in."
        templateButton={
          <CButton
            type="button"
            color="primary"
            variant="outline"
            disabled={!squadList.length}
            onClick={handleDownloadTemplate}
          >
            <CIcon icon={cilCloudDownload} className="me-1" />
            Download Scorecard Template
          </CButton>
        }
        requiredColumns={[
          <>
            Player <span className="text-medium-emphasis">(or First Name / Last Name)</span>
          </>,
          <>
            Player ID <span className="text-medium-emphasis">(optional, matched first)</span>
          </>,
          <>One column per stat, named as on the grid &mdash; RS, 4S, WKTS, CS&hellip;</>,
        ]}
        info={
          <>
            A match export can be dropped in as it comes: columns this scorecard does not use are
            ignored, and blank cells are left alone, so a player who only bowled keeps an empty
            batting line rather than a row of zeros. Rows are matched on player ID first and then on
            name &mdash; you can point any row at a different player on the next step.
          </>
        }
        fileName={fileName}
        fileDetail={`${fileRowCount} row${fileRowCount === 1 ? '' : 's'} and ${
          statColumns.length
        } stat column${statColumns.length === 1 ? '' : 's'} detected`}
        busy={busy}
        onFile={handleFile}
        onRemove={resetAll}
      />

      <div className="d-flex justify-content-between mt-4">
        <CButton type="button" color="secondary" variant="outline" onClick={onCancel}>
          Cancel
        </CButton>
        <CButton type="button" color="primary" disabled={!rows.length} onClick={() => goToStep(2)}>
          Review File
        </CButton>
      </div>
    </>
  )

  const renderValidate = () => (
    <>
      <ImportFileBar
        fileName={fileName}
        detail={`${fileRowCount} rows · ${statColumns.map((c) => c.label).join(', ')}`}
        onReplace={resetAll}
      />

      <div className="d-flex gap-3 flex-wrap mb-3">
        <StatTile label="Valid" value={summary.valid} tone="success" icon={cilCheckCircle} />
        <StatTile
          label="Needs Attention"
          value={summary.needsAttention}
          tone="warning"
          icon={cilWarning}
        />
        <StatTile
          label="Overwrites Saved Scores"
          value={overwriteCount}
          tone="info"
          icon={cilSync}
        />
      </div>

      {ignoredHeaders.length > 0 && (
        <InfoBox color="light">
          Ignored column{ignoredHeaders.length === 1 ? '' : 's'}:{' '}
          <strong>{ignoredHeaders.join(', ')}</strong>. Nothing in{' '}
          {ignoredHeaders.length === 1 ? 'it' : 'them'} is imported.
        </InfoBox>
      )}

      <CCard className="border-secondary-subtle rounded-4 overflow-hidden">
        <div className="table-responsive">
          <CTable align="middle" hover className="mb-0 ge-import-table">
            <CTableHead style={{ backgroundColor: 'var(--cui-tertiary-bg, #f8f9fa)' }}>
              <CTableRow>
                <CTableHeaderCell className="ps-4">Row</CTableHeaderCell>
                <CTableHeaderCell>Name In File</CTableHeaderCell>
                <CTableHeaderCell style={{ minWidth: 260 }}>Squad Player</CTableHeaderCell>
                <CTableHeaderCell>Scores</CTableHeaderCell>
                <CTableHeaderCell>Result</CTableHeaderCell>
                <CTableHeaderCell>Action</CTableHeaderCell>
              </CTableRow>
            </CTableHead>
            <CTableBody>
              {rows.map((r) => {
                const meta = RESULT_META[r.result] || RESULT_META.valid
                const matchMeta = MATCH_META[r.match]
                return (
                  <CTableRow
                    key={r.row}
                    style={r.result === 'valid' ? undefined : tintStyle(meta.accent, '0d')}
                  >
                    <CTableDataCell className="ps-4 text-medium-emphasis">{r.row}</CTableDataCell>

                    <CTableDataCell>
                      <div className="fw-semibold">{r.csv_name || '—'}</div>
                      {r.csv_player_id && <code className="small">{r.csv_player_id}</code>}
                    </CTableDataCell>

                    <CTableDataCell>
                      <CFormSelect
                        size="sm"
                        value={r.player_row_id}
                        onChange={(e) => remapRow(r.row, e.target.value)}
                      >
                        {/* Clearing a row that was pointed somewhere by hand puts it back on
                            whatever the file matched by itself, rather than emptying it. */}
                        <option value="">
                          {r.manual_id ? 'Undo manual mapping' : 'Select squad player'}
                        </option>
                        {playerOptions.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </CFormSelect>
                      {matchMeta && (
                        <CBadge
                          color={matchMeta.color}
                          shape="rounded-pill"
                          className="px-2 py-1 mt-1"
                        >
                          {matchMeta.label}
                        </CBadge>
                      )}
                      {r.result === 'ambiguous_player' && (
                        <div className="small text-medium-emphasis mt-1">
                          {r.ambiguous_count} squad players share this name — pick the right one.
                        </div>
                      )}
                    </CTableDataCell>

                    <CTableDataCell>
                      <RowScores statColumns={statColumns} values={r.values} />
                      {r.invalid_cells > 0 && (
                        <div className="small text-warning-emphasis">
                          {r.invalid_cells} cell{r.invalid_cells === 1 ? '' : 's'} could not be read
                          and {r.invalid_cells === 1 ? 'was' : 'were'} skipped.
                        </div>
                      )}
                    </CTableDataCell>

                    <CTableDataCell>
                      <CBadge color={meta.color} shape="rounded-pill" className="px-2 py-1">
                        {meta.label}
                      </CBadge>
                      {r.overwrite && (
                        <div>
                          <CBadge
                            color="info"
                            shape="rounded-pill"
                            className="px-2 py-1 mt-1"
                            title="This player already has scores saved for this innings"
                          >
                            Overwrites
                          </CBadge>
                        </div>
                      )}
                    </CTableDataCell>

                    <CTableDataCell>
                      {r.result === 'valid' && (
                        <CButton
                          type="button"
                          size="sm"
                          color={r.included ? 'danger' : 'success'}
                          variant="outline"
                          title={r.included ? 'Exclude from import' : 'Include in import'}
                          onClick={() => toggleRowIncluded(r.row)}
                        >
                          {r.included ? 'Exclude' : 'Include'}
                        </CButton>
                      )}
                    </CTableDataCell>
                  </CTableRow>
                )
              })}
            </CTableBody>
          </CTable>
        </div>
      </CCard>

      {summary.needsAttention > 0 && (
        <InfoBox color="light">
          Rows that need attention are left out of the import. Point one at the right squad player
          to fix it, or leave it — the rest will still import.
        </InfoBox>
      )}

      <div className="d-flex justify-content-between mt-3">
        <CButton type="button" color="secondary" variant="outline" onClick={() => goToStep(1)}>
          <CIcon icon={cilArrowLeft} className="me-1" />
          Back
        </CButton>
        <CButton
          type="button"
          color="primary"
          disabled={!readyRows.length}
          onClick={() => goToStep(3)}
        >
          Continue
        </CButton>
      </div>
    </>
  )

  const renderReview = () => (
    <>
      <div className="d-flex gap-3 flex-wrap mb-3">
        <StatTile label="File Rows" value={rows.length} tone="secondary" icon={cilFile} />
        <StatTile
          label="Ready to Import"
          value={readyRows.length}
          tone="success"
          icon={cilCheckCircle}
        />
        <StatTile
          label="Excluded"
          value={rows.length - readyRows.length}
          tone="warning"
          icon={cilWarning}
        />
        <StatTile
          label="Overwrites Saved Scores"
          value={overwriteCount}
          tone="info"
          icon={cilSync}
        />
      </div>

      <InfoBox>
        Importing saves these scores to {inningLabel || 'this innings'} straight away and
        recalculates fantasy points — there is no separate save step.
        {skippedCells > 0 && (
          <>
            {' '}
            {skippedCells} cell{skippedCells === 1 ? '' : 's'} in the file could not be read and{' '}
            {skippedCells === 1 ? 'is' : 'are'} left as {skippedCells === 1 ? 'it is' : 'they are'}{' '}
            on the scorecard.
          </>
        )}
      </InfoBox>

      <CCard className="border-secondary-subtle rounded-4 overflow-hidden">
        <div className="table-responsive">
          <CTable align="middle" hover className="mb-0 ge-import-table">
            <CTableHead style={{ backgroundColor: 'var(--cui-tertiary-bg, #f8f9fa)' }}>
              <CTableRow>
                <CTableHeaderCell className="ps-4">Player</CTableHeaderCell>
                {statColumns.map((c) => (
                  <CTableHeaderCell key={c.attribute_key} title={c.full_name}>
                    {c.label}
                  </CTableHeaderCell>
                ))}
              </CTableRow>
            </CTableHead>
            <CTableBody>
              {readyRows.map((r) => (
                <CTableRow key={r.row}>
                  <CTableDataCell className="ps-4">
                    <div className="fw-semibold">{r.player_name}</div>
                    {r.overwrite && (
                      <div className="small text-medium-emphasis">Replaces saved scores</div>
                    )}
                  </CTableDataCell>
                  {statColumns.map((c) => (
                    <CTableDataCell key={c.attribute_key}>
                      <DisplayValue
                        attributeKey={c.attribute_key}
                        value={r.values[c.attribute_key]}
                      />
                    </CTableDataCell>
                  ))}
                </CTableRow>
              ))}
            </CTableBody>
          </CTable>
        </div>
      </CCard>

      <div className="d-flex justify-content-between mt-3">
        <CButton type="button" color="secondary" variant="outline" onClick={() => goToStep(2)}>
          <CIcon icon={cilArrowLeft} className="me-1" />
          Back
        </CButton>
        <CLoadingButton
          type="button"
          color="success"
          loading={importing}
          disabled={!readyRows.length}
          onClick={runImport}
        >
          <CIcon icon={cilCloudUpload} className="me-1" />
          Import {readyRows.length} Player{readyRows.length === 1 ? '' : 's'}
        </CLoadingButton>
      </div>
    </>
  )

  if (!squadList.length) {
    return (
      <InfoBox color="light">
        This innings has no squad yet. Add players to the squad before importing scores.
      </InfoBox>
    )
  }

  return (
    <>
      <MiniStepper
        steps={WIZARD_STEPS}
        current={wizardStep}
        maxReached={maxWizardStep}
        onStepClick={goToStep}
      />
      <ImportErrorAlert errors={importErrors} />
      {wizardStep === 1 && renderUpload()}
      {wizardStep === 2 && renderValidate()}
      {wizardStep === 3 && renderReview()}
    </>
  )
}

export default ScorecardImport
